import { deepCopy } from "@ethersproject/properties";
// This is the only file which should instantiate new Providers.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { StaticJsonRpcProvider } from "@ethersproject/providers";
import { isPlain } from "@reduxjs/toolkit";
import {
  ChainIdLegacy,
  SupportedChainId,
  SupportedChainValue,
} from "@silo/types";
import { serverRpc } from "@silo/utils";

const CHAIN_IDS_TO_NAMES: SupportedChainValue<string> = {
  [ChainIdLegacy.MAINNET]: "mainnet",
  [ChainIdLegacy.ARBITRUM]: "arbitrum",
  [ChainIdLegacy.OPTIMISM]: "optimism",
  [ChainIdLegacy.BASE]: "base",
  [ChainIdLegacy.LOCAL]: "",
  [ChainIdLegacy.ANVIL]: "mainnet",
};

const AVERAGE_L1_BLOCK_TIME = 12 * 1000;

class AppJsonRpcProvider extends StaticJsonRpcProvider {
  private _blockCache = new Map<string, Promise<any>>();
  get blockCache() {
    // If the blockCache has not yet been initialized this block, do so by
    // setting a listener to clear it on the next block.
    if (!this._blockCache.size) {
      this.once("block", () => this._blockCache.clear());
    }
    return this._blockCache;
  }

  constructor(chainId: SupportedChainId) {
    // Including networkish allows ethers to skip the initial detectNetwork call.
    super(
      serverRpc.infura[chainId],
      /* networkish= */ { chainId, name: CHAIN_IDS_TO_NAMES[chainId] }
    );

    // NB: Third-party providers (eg MetaMask) will have their own polling intervals,
    // which should be left as-is to allow operations (eg transaction confirmation) to resolve faster.
    // Network providers (eg AppJsonRpcProvider) need to update less frequently to be considered responsive.
    this.pollingInterval = AVERAGE_L1_BLOCK_TIME;
  }

  send(method: string, params: Array<any>): Promise<any> {
    // Only cache eth_call's.
    if (method !== "eth_call") return super.send(method, params);

    // Only cache if params are serializable.
    if (!isPlain(params)) return super.send(method, params);

    const key = `call:${JSON.stringify(params)}`;
    const cached = this.blockCache.get(key);
    if (cached) {
      this.emit("debug", {
        action: "request",
        request: deepCopy({ method, params, id: "cache" }),
        provider: this,
      });
      return cached;
    }

    const result = super.send(method, params);
    this.blockCache.set(key, result);
    return result;
  }
}

/**
 * This guard exists to make sure that the RPC_PROVIDERS object is properly
 * checked when adding a new chain. Adding ANVIL resulted in a strange chain
 * mismatch error. The app still works by commenting it out and casting the
 * object as any.
 *
 * For any future chains, try adding them to this object.
 */
export const rpcProviderGuard: SupportedChainValue<true> = {
  [ChainIdLegacy.MAINNET]: true,
  [ChainIdLegacy.ARBITRUM]: true,
  [ChainIdLegacy.OPTIMISM]: true,
  [ChainIdLegacy.BASE]: true,
  [ChainIdLegacy.ANVIL]: true,
  [ChainIdLegacy.LOCAL]: true,
};

export const RPC_PROVIDERS = (): SupportedChainValue<AppJsonRpcProvider> =>
  ({
    [ChainIdLegacy.MAINNET]: new AppJsonRpcProvider(ChainIdLegacy.MAINNET),
    [ChainIdLegacy.ARBITRUM]: new AppJsonRpcProvider(ChainIdLegacy.ARBITRUM),
    [ChainIdLegacy.OPTIMISM]: new AppJsonRpcProvider(ChainIdLegacy.OPTIMISM),
    [ChainIdLegacy.BASE]: new AppJsonRpcProvider(ChainIdLegacy.BASE),
    // [ChainIdLegacy.ANVIL]: new AppJsonRpcProvider(ChainIdLegacy.ANVIL),
  } as any);
